import { useState } from 'react';

import { InfoIconTooltip, Input } from '@components';
import AmountInput from '@components/amount-input/AmountInput';
import MonthCard from '@components/payment-collection/components/MonthCard';
import RadioButton from '@components/radio-button/RadioButton';
import { PaymentCollectionType } from '@features/group-management/constants';
import Modal from '@layouts/modal/Modal.v2';
import { FormDataType } from '@types';

import AttendanceSection from '../../v1/attendance-section/AttendanceSection';
import InstallmentSection from '../../v1/feeDetailsInput/InstallmentSection';

interface AttendanceBasedFeeSectionProps {
  formData: FormDataType;
  onInputChange: (field: string, value: any) => void;
}

type PaymentRequestType = 'monthly' | 'session_based';

const getMonthDayLabel = (activationDate: string): string => {
  if (activationDate === 'first_day') return '1st day of the month';
  if (activationDate === 'last_day') return 'Last day of the month';
  if (activationDate && !Number.isNaN(Number(activationDate))) {
    const n = Number(activationDate);
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    const suffix = s[(v - 20) % 10] || s[v] || s[0];
    return `${n}${suffix} day of the month`;
  }
  return '1st day of the month';
};

const AttendanceBasedFeeSection = ({
  formData,
  onInputChange
}: AttendanceBasedFeeSectionProps) => {
  const [isBillingModalOpen, setIsBillingModalOpen] = useState(false);
  const [tempActivationDate, setTempActivationDate] = useState<string>('');

  const selectedMonthLabel = getMonthDayLabel(
    formData.activationDate || 'first_day'
  );

  const handlePaymentRequestTypeChange = (type: PaymentRequestType) => {
    if (type === 'monthly') {
      onInputChange('paymentCollectionType', PaymentCollectionType.MONTHLY);
      onInputChange('intervalFrequency', '1');
      if (
        !formData.activationDate ||
        formData.paymentCollectionType !== PaymentCollectionType.MONTHLY
      ) {
        onInputChange('activationDate', 'first_day');
      }
      onInputChange('sessionsPerCycle', undefined);
    } else if (type === 'session_based') {
      onInputChange(
        'paymentCollectionType',
        PaymentCollectionType.SESSION_BASED
      );
      onInputChange('intervalFrequency', '1');
      onInputChange('activationDate', '');
    }
  };

  const handleOpenBillingModal = () => {
    setTempActivationDate(formData.activationDate || 'first_day');
    setIsBillingModalOpen(true);
  };

  const handleBillingModalApply = () => {
    onInputChange('activationDate', tempActivationDate);
    onInputChange('intervalFrequency', '1');
    setIsBillingModalOpen(false);
  };

  const handleBillingModalCancel = () => {
    setTempActivationDate('');
    setIsBillingModalOpen(false);
  };

  const handleSessionsChange = (value: string) => {
    const num = value ? Number(value) : undefined;
    onInputChange('sessionsPerCycle', num);
  };

  const handleAmountChange = (value: string) => {
    onInputChange('amount', value);
  };

  const isMonthly =
    formData.paymentCollectionType === PaymentCollectionType.MONTHLY;
  const isSessionBased =
    formData.paymentCollectionType === PaymentCollectionType.SESSION_BASED;

  return (
    <div className="rounded-xl border border-cofeeGreyBlue200 bg-cofeeNeutral1 py-4">
      <h3 className="border-b border-cofeeGreyBlue200 px-6 pb-4 text-lg font-semibold text-cofeeNeutral10">
        Attendance-based fee details
      </h3>
      <div className="grid gap-y-6 p-6">
        {/* Rate per session */}
        <div>
          <label className="mb-1 block text-sm font-semibold text-cofeeNeutral10">
            Rate per session
          </label>
          <div className="flex items-center gap-3">
            <AmountInput
              name="rate-per-session"
              value={formData.amount}
              onAmountChange={handleAmountChange}
              placeholder="0"
              className="!w-72"
              suffixText="/ Session"
            />
          </div>
          <div className="mt-1.5 flex items-center gap-1 text-xs text-cofeeNeutral7">
            <InfoIconTooltip tooltipText="" id="rate-per-session-info" />
            {/* currency Amount */}
            <div className="flex items-center gap-0.5">
              <span>Enter the amount to charge per session attended. </span>
            </div>
          </div>
        </div>

        {/* When should payment be requested? */}
        <div>
          <p className="mb-3 font-semibold text-cofeeNeutral10">
            When should payment be requested?
          </p>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {/* Monthly option */}
            <div>
              <RadioButton
                buttonId="monthly"
                value="monthly"
                label="Monthly"
                isSelected={isMonthly}
                onChange={() => handlePaymentRequestTypeChange('monthly')}
                labelStyle="!text-cofeeNeutral10 !font-medium"
              />

              <div
                className={`mt-3 ${
                  !isMonthly ? 'pointer-events-none opacity-50' : ''
                }`}
              >
                <Input
                  leftIconName="calendar"
                  placeholder="Select billing period"
                  value={selectedMonthLabel}
                  onClick={handleOpenBillingModal}
                  className="w-full"
                  readOnly={!isMonthly}
                />
                <div className="mt-2 flex items-start gap-1 text-xs text-cofeeNeutral7">
                  <InfoIconTooltip
                    tooltipText="Attendance can be edited until the billing date"
                    id="monthly-info"
                  />
                  <span>
                    Attendance can be edited until the billing date. Payment
                    links are sent the next day.
                  </span>
                </div>
              </div>
              {isBillingModalOpen && (
                <Modal
                  title="Billing period"
                  onClose={handleBillingModalCancel}
                  primaryButtonLabel="Apply"
                  primaryButtonClick={handleBillingModalApply}
                  secondaryButtonLabel="Cancel"
                  secondaryButtonClick={handleBillingModalCancel}
                >
                  <MonthCard
                    handleActivationDate={(value: string | Date) =>
                      setTempActivationDate(String(value))
                    }
                    selectedDate={tempActivationDate}
                    type={PaymentCollectionType.MONTHLY}
                    handleIntervalFrequency={() => {}}
                  />
                </Modal>
              )}
            </div>

            {/* Session-based option */}
            <div>
              <RadioButton
                buttonId="session_based"
                value="session_based"
                label="After a specific number of sessions"
                isSelected={isSessionBased}
                onChange={() => handlePaymentRequestTypeChange('session_based')}
                labelStyle="!text-cofeeNeutral10 !font-medium"
              />
              <div
                className={`mt-3 ${
                  !isSessionBased ? 'pointer-events-none opacity-50' : ''
                }`}
              >
                <div className="flex items-center gap-2">
                  {/* replace inpt with Input component */}
                  <Input
                    type="number"
                    min={1}
                    value={formData.sessionsPerCycle || undefined}
                    onChange={(e) => handleSessionsChange(e.target.value)}
                    placeholder="0"
                    className="w-full"
                    suffixText="Session"
                  />
                </div>
                <div className="mt-2 flex items-start gap-1 text-xs text-cofeeNeutral7">
                  <InfoIconTooltip
                    tooltipText="Payment link is sent when a student completes this many sessions"
                    id="session-info"
                  />
                  <span>
                    Payment link is sent automatically when a student completes
                    this many sessions
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <hr className="border-cofeeGreyBlue100" />

        {/* Accept payments as installments */}
        <InstallmentSection
          formData={formData}
          onInputChange={onInputChange}
          isEnabled={false} // Update this true if installment allow for always
        />

        {/* Divider */}
        <hr className="border-cofeeGreyBlue100" />

        {/* Working Days + Notify absentees */}
        <AttendanceSection
          onInputChange={onInputChange}
          workingDays={formData.workingDays}
          isSmsAlertEnabled={formData.isSendAttendanceAlertEnabled}
          className="!mt-0"
        />
      </div>
    </div>
  );
};

export default AttendanceBasedFeeSection;
